import React from 'react';
import { Modal, Table } from 'antd';
import { useTranslation } from 'react-i18next';

interface FailedOrder {
    trackingNumber: string;
    errorCode?: string;
    message?: string;
}

interface FailedOrdersModalProps {
    visible: boolean;
    failedOrders: FailedOrder[];
    onClose: () => void;
}

const FailedOrdersModal: React.FC<FailedOrdersModalProps> = ({ visible, failedOrders, onClose }) => {
    const { t } = useTranslation();

    if (!visible || failedOrders.length === 0) {
        return null;
    }

    // 根据errorCode获取国际化的错误消息
    const getErrorMessage = (errorCode?: string, message?: string) => {
        if (!errorCode) {
            return message || t('errors.system.unknownError');
        }
        
        // 特殊处理ORDER_STATUS_NOT_ALLOW_SET_DELIVERED错误码
        if (errorCode === 'ORDER_STATUS_NOT_ALLOW_SET_DELIVERED' && message) {
            // 从message中提取状态名称（删除" status does not allow set delivered"部分）
            const statusName = message.replace(' status does not allow set delivered', '');
            
            // 尝试获取状态名称的国际化翻译
            const statusKey = `enum.overallStatus.${statusName}`;
            const translatedStatus = t(statusKey);
            
            // 如果找到了状态翻译，使用翻译后的状态名称
            const finalStatusName = translatedStatus !== statusKey ? translatedStatus : statusName;
            
            // 拼接状态名称和错误信息
            return `${finalStatusName}${t('wms.parcels.messages.statusNotAllowSetDelivered')}`;
        }

        switch (errorCode) {
            case 'EMPTY_POD_IMAGE_LIST':
                return t('wms.parcels.messages.emptyPodImageList');
            case 'PARCEL_NOT_FOUND':
                return t('wms.parcels.messages.parcelNotFound');
            case 'DELIVERY_TASK_NOT_EXIST':
                return t('wms.parcels.messages.deliveryTaskNotExist');
            case 'POD_IMAGE_MAST_3_to_6':
                return t('wms.parcels.messages.podPhotosMustBe3To6');
            case 'DELIVERY_FAILED_REASON_NOT_ALLOW_SET_DELIVERED':
                return t('wms.parcels.messages.deliveryFailedReasonNotAllowSetDelivered');
        }
        // 如果没有找到翻译，返回原始message
        return message || t('errors.system.unknownError');
    };

    // 定义表格列
    const columns = [
        {
            title: t('wms.parcels.messages.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: 210,
        },
        {
            title: t('wms.parcels.messages.errorReason'),
            dataIndex: 'errorReason',
            key: 'errorReason',
        },
    ];

    // 准备表格数据
    const tableData = failedOrders.map((order, index) => ({
        key: index,
        trackingNumber: order.trackingNumber,
        errorReason: getErrorMessage(order.errorCode, order.message),
    }));

    return (
        <Modal
            title={t('wms.parcels.messages.failedOrdersTitle')}
            open={visible}
            onOk={onClose}
            onCancel={onClose}
            width={800}
            okText={t('components.button.ok')}
        >
            <div>
                <p style={{ marginBottom: '16px' }}>
                    {t('wms.parcels.messages.failedOrdersDescription')}
                </p>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    size="small"
                    scroll={{ y: 300 }}
                />
            </div>
        </Modal>
    );
};

export default FailedOrdersModal;
